import { client } from './client';
import type { ApiResponse } from '@/types/api';
import type {
  PracticeSeedListParams,
  PracticeSeedListResponse,
  PracticeSeedDetailResponse,
  CreatePracticeSeedInput,
  UpdatePracticeSeedInput,
  PracticeSeedItem,
  PracticeSessionListParams,
  PracticeSessionListResponse,
  StudentPersonalizedPracticesResponse,
  SeedsAnalyticsResponse,
  AIInteractionListParams,
  AIInteractionListResponse,
  BulkDeleteInput,
  BulkUpdateInput,
  BulkCreateInput,
  BulkOperationResult,
  BulkRestoreInput,
  BulkAssignSeedsInput,
  BulkAssignSeedsResult,
  SeedExportRequest,
  SeedImportReport,
  SeedImportSummary,
  GenerateSeedDraftsInput,
  GenerateSeedDraftsResponse,
  ImportFormat,
  ImportConflictStrategy,
} from '@/types/seeds';

// ---------- Dashboard (teacher + admin) ----------

/**
 * Serialize PracticeSeedListParams for axios — arrays become comma-separated
 * strings so the Go handler's splitCSV helper can parse them.
 */
function serializeListParams(p?: PracticeSeedListParams): Record<string, string | number | boolean | undefined> {
  if (!p) return {};
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length > 0) out[k] = v.join(',');
    } else {
      out[k] = v as string | number | boolean;
    }
  }
  return out;
}

export async function listSeeds(
  params?: PracticeSeedListParams,
): Promise<PracticeSeedListResponse> {
  const { data } = await client.get<ApiResponse<PracticeSeedListResponse>>(
    '/api/v1/dashboard/seeds',
    { params: serializeListParams(params) },
  );
  return data.data;
}

export async function getSeedDetail(id: string): Promise<PracticeSeedDetailResponse> {
  const { data } = await client.get<ApiResponse<PracticeSeedDetailResponse>>(
    `/api/v1/dashboard/seeds/${id}`,
  );
  return data.data;
}

export async function listPracticeSessions(
  params?: PracticeSessionListParams,
): Promise<PracticeSessionListResponse> {
  const { data } = await client.get<ApiResponse<PracticeSessionListResponse>>(
    '/api/v1/dashboard/practice-sessions',
    { params },
  );
  return data.data;
}

export async function getStudentPersonalizedPractices(
  studentId: string,
  page = 1,
  perPage = 20,
): Promise<StudentPersonalizedPracticesResponse> {
  const { data } = await client.get<ApiResponse<StudentPersonalizedPracticesResponse>>(
    `/api/v1/dashboard/students/${studentId}/personalized-practices`,
    { params: { page, per_page: perPage } },
  );
  return data.data;
}

export async function getSeedsAnalytics(): Promise<SeedsAnalyticsResponse> {
  const { data } = await client.get<ApiResponse<SeedsAnalyticsResponse>>(
    '/api/v1/dashboard/seeds/analytics',
  );
  return data.data;
}

// ---------- Admin ----------

export async function createSeed(
  input: CreatePracticeSeedInput,
): Promise<PracticeSeedItem> {
  const { data } = await client.post<ApiResponse<PracticeSeedItem>>(
    '/api/v1/admin/seeds',
    input,
  );
  return data.data;
}

export async function updateSeed(
  id: string,
  input: UpdatePracticeSeedInput,
): Promise<void> {
  await client.put(`/api/v1/admin/seeds/${id}`, input);
}

export async function deleteSeed(id: string): Promise<void> {
  await client.delete(`/api/v1/admin/seeds/${id}`);
}

export async function listAIInteractions(
  params?: AIInteractionListParams,
): Promise<AIInteractionListResponse> {
  const { data } = await client.get<ApiResponse<AIInteractionListResponse>>(
    '/api/v1/admin/ai-interactions',
    { params },
  );
  return data.data;
}

// ---------- SPEC-014: Bulk operations ----------

export async function bulkDeleteSeeds(input: BulkDeleteInput): Promise<BulkOperationResult> {
  const { data } = await client.post<ApiResponse<BulkOperationResult>>(
    '/api/v1/admin/seeds/bulk-delete',
    input,
  );
  return data.data;
}

export async function bulkUpdateSeeds(input: BulkUpdateInput): Promise<{ updated: number }> {
  const { data } = await client.post<ApiResponse<{ updated: number }>>(
    '/api/v1/admin/seeds/bulk-update',
    input,
  );
  return data.data;
}

export async function bulkCreateSeeds(input: BulkCreateInput): Promise<{ created: number; seeds: PracticeSeedItem[] }> {
  const { data } = await client.post<ApiResponse<{ created: number; seeds: PracticeSeedItem[] }>>(
    '/api/v1/admin/seeds/bulk-create',
    input,
  );
  return data.data;
}

export async function restoreSeed(id: string): Promise<void> {
  await client.post(`/api/v1/admin/seeds/${id}/restore`);
}

export async function bulkRestoreSeeds(input: BulkRestoreInput): Promise<{ restored: number }> {
  const { data } = await client.post<ApiResponse<{ restored: number }>>(
    '/api/v1/admin/seeds/bulk-restore',
    input,
  );
  return data.data;
}

export async function bulkAssignSeeds(
  input: BulkAssignSeedsInput,
): Promise<BulkAssignSeedsResult> {
  const { data } = await client.post<ApiResponse<BulkAssignSeedsResult>>(
    '/api/v1/admin/seeds/bulk-assign',
    input,
  );
  return data.data;
}

// ---------- SPEC-014: Import / export ----------

export async function validateSeedImport(file: File, format: ImportFormat): Promise<SeedImportReport> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('format', format);
  const { data } = await client.post<ApiResponse<SeedImportReport>>(
    '/api/v1/admin/seeds/import/validate',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data.data;
}

export async function commitSeedImport(
  file: File,
  format: ImportFormat,
  strategy: ImportConflictStrategy,
  skipInvalid: boolean,
): Promise<SeedImportSummary> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('format', format);
  formData.append('strategy', strategy);
  formData.append('skip_invalid', String(skipInvalid));
  const { data } = await client.post<ApiResponse<SeedImportSummary>>(
    '/api/v1/admin/seeds/import/commit',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data.data;
}

export async function exportSeeds(request: SeedExportRequest): Promise<{ blob: Blob; filename: string }> {
  const response = await client.post(
    '/api/v1/admin/seeds/export',
    request,
    { responseType: 'blob' },
  );
  const blob = response.data as Blob;
  // Extract filename from Content-Disposition.
  const cd = response.headers['content-disposition'] as string | undefined;
  let filename = `seeds.${request.format}`;
  if (cd) {
    const match = /filename=(?:"([^"]+)"|([^;]+))/.exec(cd);
    if (match) filename = match[1] ?? match[2] ?? filename;
  }
  return { blob, filename };
}

// ---------- SPEC-014: AI draft generation ----------

export async function generateSeedDrafts(
  input: GenerateSeedDraftsInput,
): Promise<GenerateSeedDraftsResponse> {
  const { data } = await client.post<ApiResponse<GenerateSeedDraftsResponse>>(
    '/api/v1/admin/seeds/ai-generate',
    input,
  );
  return data.data;
}
